import apiService from './api';

class UserService {
  // Get user profile with caching
  async getProfile() {
    try {
      const data = await apiService.get('/api/user/profile', {
        cacheTimeout: 120000 // Cache profile for 2 minutes
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Update user profile
  async updateProfile(profileData) {
    try {
      const res = await apiService.put('/api/user/profile', profileData);
      apiService.clearCache('GET:/api/user/profile');
      const row = res && typeof res === 'object' && res.data != null ? res.data : res;
      return { success: true, data: row };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Upload profile picture
  async uploadProfilePicture(file) {
    try {
      const formData = new FormData();
      formData.append('profile_picture', file);
      
      const data = await apiService.postFormData('/api/user/profile/upload-picture', formData);
      // Clear profile cache after picture upload
      apiService.clearCache('GET:/api/user/profile');
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async deleteProfilePicture() {
    try {
      const data = await apiService.delete('/api/user/profile/delete-picture');
      apiService.clearCache('GET:/api/user/profile');
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Get user settings with caching
  async getSettings() {
    try {
      const data = await apiService.get('/api/user/settings', {
        cacheTimeout: 300000 // Cache settings for 5 minutes
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Update user settings
  async updateSettings(settings) {
    try {
      const data = await apiService.put('/api/user/settings', settings);
      // Clear settings cache after update
      apiService.clearCache('GET:/api/user/settings');
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Delete user account
  async deleteAccount() {
    try {
      await apiService.delete('/api/user/account');
      // Clear all caches on account deletion
      apiService.clearCache();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Search users by email/username for collaboration
  async searchUsers(query, bagId = null) {
    try {
      if (!query || query.trim().length < 2) {
        return { success: true, data: [] };
      }

      const params = { q: query.trim() };
      if (bagId) {
        params.exclude_bag_id = bagId; // Exclude users already collaborating on this bag
      }

      const data = await apiService.get('/api/user/search', {
        params,
        cacheTimeout: 30000 // Cache search results for 30 seconds
      });
      
      return { success: true, data: data.users || [] };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Invite collaborator by username (unified endpoint for both existing users and email invites)
  async inviteCollaborator(bagId, userEmail = null, username = null, user_id = null, permission = 'edit') {
    try {
      const payload = {
        user_email: userEmail ? userEmail.trim().toLowerCase() : null,
        // username and user_id intentionally omitted per new requirements
        // backend enforces edit permission and user existence
      };

      const data = await apiService.post(`/api/bag/${bagId}/invite`, payload);
      
      // Clear bag-related caches
      apiService.clearCache(`GET:/api/bag/${bagId}/collaborators`);
      apiService.clearCache('GET:/api/bags');
      
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Invite user by email (alias for consistency with component)
  async inviteByEmail(bagId, email, permission = 'edit') {
    return this.inviteCollaborator(bagId, email, null, null, 'edit');
  }

  // Get bag collaborators
  async getBagCollaborators(bagId) {
    try {
      const data = await apiService.get(`/api/bag/${bagId}/collaborators`, {
        cacheTimeout: 60000 // Cache collaborators for 1 minute
      });
      return { success: true, data: data.collaborators || data || [] };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Get notifications (collaboration invites)
  async getNotifications(userId = null) {
    try {
      // If no userId provided, get for current user - backend should handle this
      const endpoint = `/api/bag/user/${userId}/collaborations`;
      
      const data = await apiService.get(endpoint, {
        cacheTimeout: 30000 // Cache notifications for 30 seconds
      });
      
      // Transform the response to match the expected format in the component
      const notifications = data.collaborations?.map(collaboration => ({
        id: collaboration.collaboration_id,
        status: collaboration.status,
        permission: collaboration.permission,
        created_at: collaboration.invited_at,
        accepted_at: collaboration.accepted_at,
        bag_id: collaboration.bag.id,
        bag_name: collaboration.bag.name,
        bag_description: collaboration.bag.description,
        bag_is_public: collaboration.bag.is_public,
        inviter_id: collaboration.bag.owner_id,
        inviter_name: collaboration.bag.owner_name,
        inviter_username: collaboration.bag.owner_username,
        inviter_profile_picture: null
      })) || [];
      
      return { 
        success: true, 
        data: notifications,
        summary: data.summary || { total: 0, pending: 0, accepted: 0 }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Accept collaboration invitation
  async acceptInvitation(bagId) {
    try {
      const data = await apiService.post(`/api/bag/${bagId}/accept`);
      
      // Clear bag-related caches
      apiService.clearCache(`GET:/api/bag/${bagId}/collaborators`);
      apiService.clearCache('GET:/api/bags');
      apiService.clearCache('GET:/api/bag/user/collaborations'); // Clear collaborations cache
      
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Decline collaboration invitation
  async declineInvitation(bagId) {
    try {
      const data = await apiService.post(`/api/bag/${bagId}/decline`);
      
      // Clear collaborations cache
      apiService.clearCache('GET:/api/bag/user/collaborations');
      
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Remove collaborator from bag (owner only)
  async removeCollaborator(bagId, collaboratorId) {
    try {
      await apiService.delete(`/api/bag/${bagId}/collaborators/${collaboratorId}`);
      
      // Clear bag-related caches
      apiService.clearCache(`GET:/api/bag/${bagId}/collaborators`);
      apiService.clearCache('GET:/api/bags');
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Update collaborator permissions (if needed - not in your endpoints but commonly used)
  async updateCollaboratorPermission(bagId, collaboratorId, permission) {
    try {
      const payload = { permission };
      const data = await apiService.put(`/api/bag/${bagId}/collaborators/${collaboratorId}`, payload);
      
      // Clear bag-related caches
      apiService.clearCache(`GET:/api/bag/${bagId}/collaborators`);
      
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Get list of all users
  async getUsersList() {
    try {
      const data = await apiService.get('/api/user/list', {
        cacheTimeout: 60000 // Cache users list for 1 minute
      });
      return { success: true, data: data.users || data || [] };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new UserService();
